import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '@env/environment';

import {
  ClaimVoorbereidenRequest, ClaimVoorbereidenResponse, Herstelbedrijven, IndienenVragenboomRequest, IndienenVragenboomResponse, IndienenVragenMailRequest,
  Plaatsnamen, ProductInfo, RaadplegenBestandenRequest, RaadplegenBestandenResponse, Vragenboom, VragenboomResponse
} from '@models/claims';
import { VraagboomAntwoord, IBaseVraagboomResultaat, Verzekering } from '@models/vragenboom';
import { HttpService } from '@crosscutting/public_api';
import { IndienenVragenboomRequestMapper } from '../mappers/indienen-vragenboom/indienen-vragenboom-request-mapper';
import { VragenboomMapper } from '../mappers/vragenboom/vragenboom-mapper';
import { VragenboomValidator } from '../validation/vragenboom.validator';
import { DataService } from '@components/public_api';

@Injectable()
export class ClaimService {
  constructor(
    private _http: HttpService,
    private _dataService: DataService) { }

  getVragenboom(sleutel: string): Observable<Vragenboom> {
    const input = {
      InputVragenboom: [{
        Kanaal: 'WEB',
        Label: 'IPO',
        Sleutel: sleutel
      }]
    };

    return this._http.post<VragenboomResponse>(environment.vragenboomUrl, input)
      .pipe(
        map(response => this._dataService.saveReferentieGegevens(response)),
        map(response => response.vragenbomen[0]),
        map(response => VragenboomMapper.map(response)),
        map(v => {
          VragenboomValidator.validate(v);
          return v;
        })
      );
  }

  getVragenboomZakelijk(sleutel: string): Observable<Vragenboom> {
    return this._http.get<VragenboomResponse>(environment.vragenboomUrl + sleutel.toLowerCase() + '.json')
      .pipe(
        map(response => this._dataService.saveReferentieGegevens(response)),
        map(response => response.vragenbomen[0]),
        map(response => VragenboomMapper.map(response)),
        map(v => {
          VragenboomValidator.validate(v);
          return v;
        })
      );
  }

  getVragenboomAfronden(claimnr: string): Observable<IndienenVragenboomResponse> {
    const input = {
      Kanaal: 'WEB',
      Claimnummer: claimnr
    };

    return this._http.post<IndienenVragenboomResponse>(environment.claimAfrondenUrl, input)
      .pipe(
        map(response => {
          if (response) {
            response = VragenboomMapper.mapIndienen(response);
            VragenboomValidator.validate(response.vragenboom);
          }
          return response;
        })
      );
  }

  getHerstellers(claimnummer: string, postcodereeks: string): Observable<Herstelbedrijven> {
    const input = {
      Claimnummer: claimnummer,
      PostcodeReeks: postcodereeks
    }

    return this._http.post<Herstelbedrijven>(environment.ophalenHerstelBedrijvenUrl, input);
  }

  getPlaatsnamen(): Observable<Plaatsnamen> {
    return this._http.post<Plaatsnamen>(environment.ophalenPlaatsnamenUrl, null);
  }

  getProductInfo(): Observable<ProductInfo> {
    const input = {
      "Product": {
        "ProductSoort": "Z19",
        "Merken": []
      }
    };

    return this._http.post<ProductInfo>(environment.productinfoUrl, input);
  }

  aanmakenClaim(polisnummer: string, contractnummer: string, vragenboom: Vragenboom,
    resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[]): Observable<IndienenVragenboomResponse> {
    const request: IndienenVragenboomRequest =
      IndienenVragenboomRequestMapper.mapAanmaken(polisnummer, contractnummer, vragenboom, resultaten);
    return this.indienenClaim(request);
  }

  aanmakenClaimMail(vragenboom: Vragenboom, resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[], verzekering: Verzekering):
    Observable<IndienenVragenboomResponse> {
    const request: IndienenVragenMailRequest = IndienenVragenboomRequestMapper.mapAanmakenMail(vragenboom, resultaten, verzekering);
    return this.indienenClaimMail(request);
  }

  afrondenClaim(claimnummer: string, subclaimnummer: string, taaknummer: string, vragenboom: Vragenboom, resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[]):
    Observable<IndienenVragenboomResponse> {
    const request: IndienenVragenboomRequest = IndienenVragenboomRequestMapper.mapBijwerken(claimnummer, subclaimnummer, taaknummer, vragenboom, resultaten);
    return this.indienenClaim(request);
  }

  bijwerkenClaim(claimnummer: string, vragenboom: Vragenboom, resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[]):
    Observable<IndienenVragenboomResponse> {
    const request: IndienenVragenboomRequest = IndienenVragenboomRequestMapper.mapBijwerken(claimnummer, '', '', vragenboom, resultaten);
    return this.indienenClaim(request);
  }

  private indienenClaim(request: IndienenVragenboomRequest): Observable<IndienenVragenboomResponse> {
    return this._http.post<IndienenVragenboomResponse>(environment.indienenVragenboomUrl, request)
      .pipe(
        map(response => {
          if (response) {
            response = VragenboomMapper.mapIndienen(response);
            VragenboomValidator.validate(response.vragenboom);
          }
          return response;
        }),
        map(v => {
          VragenboomValidator.validate(v.vragenboom);
          return v;
        })
      );
  }

  private indienenClaimMail(request: IndienenVragenMailRequest): Observable<IndienenVragenboomResponse> {
    return this._http.post<IndienenVragenboomResponse>(environment.indienenVragenboomUrl, request)
      .pipe(
        map(response => {
          if (response) {
            response = VragenboomMapper.mapIndienen(response);
          }
          return response;
        }),
        map(v => {
          VragenboomValidator.validate(v.vragenboom);
          return v;
        })
      );
  }

  claimVoorbereiden(request: ClaimVoorbereidenRequest): Observable<ClaimVoorbereidenResponse> {
    return this._http.post<ClaimVoorbereidenResponse>(environment.claimVoorbereidenUrl, request)
      .pipe(map(response => {
        return response;
      }));
  }

  raadplegenBestanden(claimnummer: string): Observable<RaadplegenBestandenResponse> {
    const request: RaadplegenBestandenRequest = new RaadplegenBestandenRequest();
    request.claimnummer = claimnummer;

    return this._http.post<RaadplegenBestandenResponse>(environment.raadplegenBestandenUrl, request)
      .pipe(map(response => {
        return response;
      }));
  }

  raadplegenBestandDetails(id: string): Observable<Blob> {
    return this._http.postReturnBlob<Blob>(environment.raadplegenBestandDetailsUrl, { bestandId: id });
  }
}
